import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { referralAPI, clientAPI, staffAPI } from '../services/api';
import PageHeader from './PageHeader';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { hasPermission } from '../utils/permissions';
import { formatDisplayDate } from '../utils/dateFormat';
import './ScAssignmentList.css';

function ScAssignmentList() {
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const [openDate, setOpenDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [openDateTo, setOpenDateTo] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [staff, setStaff] = useState([]);
  const [assigningClientId, setAssigningClientId] = useState(null);
  const [assignFeedback, setAssignFeedback] = useState(null);
  const [workloadModalOpen, setWorkloadModalOpen] = useState(false);
  const [workloadRows, setWorkloadRows] = useState([]);
  const [workloadLoading, setWorkloadLoading] = useState(false);
  const [showAssignmentInfo, setShowAssignmentInfo] = useState(false);
  const [pendingAssign, setPendingAssign] = useState(null);
  const [pendingGenerateDocs, setPendingGenerateDocs] = useState(true);
  const [detailRow, setDetailRow] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterUnassignedOnly, setFilterUnassignedOnly] = useState(false);
  const [sortKey, setSortKey] = useState('city');
  const [sortDir, setSortDir] = useState('asc');
  const canAssignStaff = hasPermission('clients.assign_staff');

  const REFERRAL_REASON_TRUNCATE = 40;
  const truncateReason = (s) => (s && s.length > REFERRAL_REASON_TRUNCATE ? `${s.slice(0, REFERRAL_REASON_TRUNCATE)}…` : s || '—');
  const langDisplay = (row) => {
    const code = row.primary_language_code ? String(row.primary_language_code).trim() : '';
    if (!code) return '—';
    return code.length <= 2 ? code.toUpperCase() : code.charAt(0).toUpperCase();
  };

  const assignmentInfoText = 'View referrals by open date. Choose a date to see who was opened that day and who is assigned as service coordinator.';

  const loadByOpenDate = async () => {
    if (!openDate) return;
    setLoading(true);
    try {
      const params = { open_date: openDate };
      if (openDateTo) params.open_date_to = openDateTo;
      const res = await referralAPI.getByOpenDate(params);
      setRows(res.data?.data ?? []);
    } catch (err) {
      console.error('Failed to load referrals by open date:', err);
      setRows([]);
      alert(err.response?.data?.error || 'Failed to load list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!openDate) return;
    loadByOpenDate();
  }, [openDate, openDateTo]);

  useEffect(() => {
    if (!canAssignStaff) return;
    staffAPI.getAll({ active: 'true' })
      .then((res) => setStaff(res.data?.data ?? res.data ?? []))
      .catch(() => setStaff([]));
  }, [canAssignStaff]);

  useEffect(() => {
    if (!assignFeedback) return;
    const t = setTimeout(() => setAssignFeedback(null), 8000);
    return () => clearTimeout(t);
  }, [assignFeedback]);

  const handleAssignStaff = async (row, newStaffId, generateDocs = true) => {
    const value = newStaffId === '' || newStaffId == null ? null : newStaffId;
    setAssigningClientId(row.client_id);
    setAssignFeedback(null);
    try {
      const response = await clientAPI.update(row.client_id, {
        assigned_staff_id: value,
        generate_documents_on_assign: generateDocs,
      });
      const postAssign = response.data?.postAssign;
      const staffName = value
        ? (() => {
            const s = staff.find((x) => x.id === value);
            return s ? [s.first_name, s.last_name].filter(Boolean).join(' ') : 'assignee';
          })()
        : null;
      const newFirst = value ? staff.find((s) => s.id === value)?.first_name : null;
      const newLast = value ? staff.find((s) => s.id === value)?.last_name : null;
      const referralStatusUpdated = postAssign?.referralStatusUpdated ?? false;
      setRows((prev) =>
        prev.map((r) =>
          r.client_id === row.client_id
            ? {
                ...r,
                assigned_staff_id: value,
                assigned_staff_first_name: newFirst,
                assigned_staff_last_name: newLast,
                ...(referralStatusUpdated ? { referral_status: 'active' } : {}),
              }
            : r
        )
      );
      const clientName = [row.first_name, row.last_name].filter(Boolean).join(' ') || 'Client';
      if (value && staffName) {
        setAssignFeedback({
          clientName,
          staffName,
          notificationSent: postAssign?.notificationSent ?? false,
          documentsGenerated: postAssign?.documentsGenerated ?? 0,
          documentsFailed: postAssign?.documentsFailed ?? 0,
          documentsGeneratedNames: postAssign?.documentsGeneratedNames ?? [],
          referralStatusUpdated,
        });
      } else if (!value) {
        setAssignFeedback({
          clientName,
          staffName: null,
          unassigned: true,
        });
      }
    } catch (err) {
      console.error('Failed to assign client:', err);
      alert(err.response?.data?.error || 'Failed to update assignment');
    } finally {
      setAssigningClientId(null);
    }
  };

  const openWorkloadModal = () => {
    setWorkloadModalOpen(true);
    setWorkloadLoading(true);
    staffAPI.getWorkload()
      .then((res) => setWorkloadRows(res.data?.data ?? []))
      .catch(() => setWorkloadRows([]))
      .finally(() => setWorkloadLoading(false));
  };

  const formatDoB = (d) => formatDisplayDate(d);
  const assignedName = (r) =>
    r.assigned_staff_first_name || r.assigned_staff_last_name
      ? [r.assigned_staff_first_name, r.assigned_staff_last_name].filter(Boolean).join(' ')
      : 'Unassigned';

  const STATUS_META = {
    incomplete:      { label: 'Incomplete',      cls: 'sc-status-badge--incomplete' },
    waiting:         { label: 'Waiting',         cls: 'sc-status-badge--waiting' },
    active:          { label: 'Active',          cls: 'sc-status-badge--active' },
    closing_out:     { label: 'Closing out',     cls: 'sc-status-badge--closing-out' },
    transferring_out:{ label: 'Transferring out',cls: 'sc-status-badge--transferring-out' },
    transferring_in: { label: 'Transferring in', cls: 'sc-status-badge--transferring-in' },
    closed:          { label: 'Closed',          cls: 'sc-status-badge--closed' },
  };
  const statusBadge = (status) => {
    const meta = STATUS_META[status] || { label: status || '—', cls: '' };
    return <span className={`sc-status-badge ${meta.cls}`}>{meta.label}</span>;
  };

  const visibleRows = rows
    .filter((r) => {
      if (filterStatus !== 'all' && r.referral_status !== filterStatus) return false;
      if (filterUnassignedOnly) {
        // "Unassigned" means the select shows Unassigned — i.e. no active SC assigned.
        // Matches the dropdown which only lists active staff.
        const hasActiveAssignment = r.assigned_staff_id &&
          staff.some((s) => String(s.id) === String(r.assigned_staff_id));
        if (hasActiveAssignment) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const va = String(a[sortKey] ?? '').trim().toLowerCase();
      const vb = String(b[sortKey] ?? '').trim().toLowerCase();
      const cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const handleCitySort = () => {
    if (sortKey === 'city') {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey('city');
      setSortDir('asc');
    }
  };

  return (
    <div className="sc-assignment-list">
      <div className="sc-assignment-header">
        <div className="sc-assignment-header-title-row">
          <PageHeader
            title={
              <span className="sc-assignment-title-with-info">
                SC Assignment
                <button
                  type="button"
                  className="sc-assignment-info-icon"
                  onClick={() => setShowAssignmentInfo((v) => !v)}
                  aria-expanded={showAssignmentInfo}
                  aria-controls="sc-assignment-info-text"
                  aria-label={showAssignmentInfo ? 'Hide explanation' : 'Show explanation'}
                >
                  ⓘ
                </button>
              </span>
            }
          >
            {canAssignStaff && (
              <button type="button" className="sc-assignment-workload-btn" onClick={openWorkloadModal}>
                SC Workload
              </button>
            )}
          </PageHeader>
        </div>
        {showAssignmentInfo && (
          <p id="sc-assignment-info-text" className="sc-assignment-info-text">
            {assignmentInfoText}
          </p>
        )}
      </div>
      <div className="sc-assignment-list-filters">
        <label>
          <span>Referral open from</span>
          <input
            type="date"
            value={openDate}
            onChange={(e) => setOpenDate(e.target.value)}
            aria-label="Referral open from"
          />
        </label>
        <label>
          <span>Referral open to</span>
          <input
            type="date"
            value={openDateTo}
            onChange={(e) => setOpenDateTo(e.target.value)}
            aria-label="Referral open to"
            placeholder="Optional"
          />
        </label>
        <label>
          <span>Referral status</span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} aria-label="Filter by referral status">
            <option value="all">All</option>
            <option value="incomplete">Incomplete</option>
            <option value="waiting">Waiting</option>
            <option value="active">Active</option>
            <option value="closing_out">Closing out</option>
            <option value="transferring_out">Transferring out</option>
            <option value="transferring_in">Transferring in</option>
            <option value="closed">Closed</option>
          </select>
        </label>
        <label className="sc-assignment-filter-check-label">
          <input
            type="checkbox"
            checked={filterUnassignedOnly}
            onChange={(e) => setFilterUnassignedOnly(e.target.checked)}
          />
          <span>Unassigned only</span>
        </label>
      </div>

      {assignFeedback && (
        <div className={`sc-assignment-list-feedback${assignFeedback.unassigned ? ' sc-assignment-list-feedback--warn' : ''}`} role="status">
          {assignFeedback.unassigned ? (
            <>
              <strong>{assignFeedback.clientName}</strong> has been unassigned.{' '}
              <em>Reminder: you may want to update the referral status if it should no longer be Active.</em>
            </>
          ) : (
            <>
              <strong>{assignFeedback.clientName}</strong> assigned to <strong>{assignFeedback.staffName}</strong>.
              {assignFeedback.notificationSent ? ' Notification sent to their inbox.' : ''}
              {assignFeedback.referralStatusUpdated && ' Referral status updated to Active.'}
              {assignFeedback.documentsGenerated > 0 && (
                <> {assignFeedback.documentsGeneratedNames?.length > 0
                  ? `${assignFeedback.documentsGeneratedNames.join(', ')} have been generated and stored in the client folder.`
                  : `${assignFeedback.documentsGenerated} document(s) have been generated and stored in the client folder.`}</>
              )}
              {assignFeedback.documentsFailed > 0 && (
                <> ({assignFeedback.documentsFailed} document(s) failed to generate.)</>
              )}
            </>
          )}
        </div>
      )}

      <div className="sc-assignment-list-table-wrap">
        <table className="sc-assignment-list-table">
          <thead>
            <tr>
              <th className="sc-assignment-th-icon" aria-label="More details" />
              <th>Assigned SC</th>
              <th className="sc-assignment-th-rereferral" aria-label="Re-referral" />
              <th className="sc-assignment-th-rereferral" aria-label="Sibling in EI" />
              <th>Client</th>
              <th>Status</th>
              <th className="sc-assignment-th-narrow">Opened</th>
              <th className="sc-assignment-th-narrow" title="Primary language">Lang</th>
              <th className="sc-assignment-th-sortable" onClick={handleCitySort} aria-sort={sortKey === 'city' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                City{sortKey === 'city' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}
              </th>
              <th className="sc-assignment-th-narrow" title="Age in months">Age (mo)</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={11} className="no-data loading-cell">Loading…</td>
              </tr>
            ) : visibleRows.length === 0 ? (
              <tr>
                <td colSpan={11} className="no-data">
                  {rows.length === 0 ? 'No referrals opened on this date.' : 'No referrals match the current filters.'}
                </td>
              </tr>
            ) : (
              visibleRows.map((row) => {
                const clientName = [row.last_name, row.first_name].filter(Boolean).length
                  ? [row.last_name, row.first_name].filter(Boolean).join(', ')
                  : '—';
                const isExpanded = detailRow?.referral_id === row.referral_id;
                return (
                <React.Fragment key={row.referral_id}>
                  <tr
                    onClick={() => navigate(tenantPath(`/client/${row.client_id}`))}
                    className="clickable-row"
                  >
                    <td className="sc-assignment-td-icon" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="sc-assignment-detail-btn"
                        onClick={() => setDetailRow((prev) => (prev?.referral_id === row.referral_id ? null : row))}
                        aria-label={isExpanded ? `Hide details for ${clientName}` : `Details for ${clientName}`}
                        title={isExpanded ? 'Hide details' : 'View details'}
                        aria-expanded={isExpanded}
                      >
                        ℹ
                      </button>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {canAssignStaff ? (
                        <select
                          className="sc-assignment-list-select"
                          value={row.assigned_staff_id ?? ''}
                          onChange={(e) => {
                            setPendingGenerateDocs(true);
                            setPendingAssign({ row, value: e.target.value });
                          }}
                          disabled={assigningClientId === row.client_id}
                          aria-label={`Assign ${clientName}`}
                        >
                          <option value="">Unassigned</option>
                          {staff.map((s) => (
                            <option key={s.id} value={s.id}>
                              {[s.first_name, s.last_name].filter(Boolean).join(' ') || s.id}
                            </option>
                          ))}
                        </select>
                      ) : (
                        assignedName(row)
                      )}
                    </td>
                    <td className="sc-assignment-td-rereferral">
                      {(row.re_referral === true || row.re_referral === 'true') && (
                        <span className="sc-assignment-rereferral-badge" title="Re-referral">R</span>
                      )}
                    </td>
                    <td className="sc-assignment-td-rereferral">
                      {(row.sibling_in_ei === true || row.sibling_in_ei === 'true') && (
                        <span className="sc-assignment-sibling-badge" title="Sibling in EI">S</span>
                      )}
                    </td>
                    <td>{clientName}</td>
                    <td>{statusBadge(row.referral_status)}</td>
                    <td className="sc-assignment-td-narrow">{formatDisplayDate(row.referral_open_date)}</td>
                    <td className="sc-assignment-td-narrow">{langDisplay(row)}</td>
                    <td>{row.city || '—'}</td>
                    <td className="sc-assignment-td-narrow">{row.age_in_months != null ? row.age_in_months : '—'}</td>
                    <td className="sc-assignment-reason-cell">{truncateReason(row.referral_reason)}</td>
                  </tr>
                  {isExpanded && (
                    <tr className="sc-assignment-detail-expanded-row" onClick={(e) => e.stopPropagation()}>
                      <td colSpan={11} className="sc-assignment-detail-expanded-cell">
                        <dl className="sc-detail-dl sc-detail-dl-inline">
                          <dt>Referral status</dt><dd>{statusBadge(row.referral_status)}</dd>
                          <dt>Open date</dt><dd>{formatDisplayDate(row.referral_open_date)}</dd>
                          <dt>DOB</dt><dd>{formatDoB(row.date_of_birth)}</dd>
                          <dt>Age (months)</dt><dd>{row.age_in_months != null ? row.age_in_months : '—'}</dd>
                          <dt>Primary language</dt><dd>{row.primary_language_description || row.primary_language_code || '—'}</dd>
                          <dt>City</dt><dd>{row.city || '—'}</dd>
                          <dt>Referral reason</dt><dd className="sc-detail-reason">{row.referral_reason || '—'}</dd>
                          <dt>Re-referral</dt><dd>{row.re_referral === true || row.re_referral === 'true' ? 'Yes' : 'No'}</dd>
                          <dt>Sibling in EI</dt><dd>{row.sibling_in_ei === true || row.sibling_in_ei === 'true' ? 'Yes' : 'No'}</dd>
                          {(row.sibling_in_ei === true || row.sibling_in_ei === 'true') && (
                            <>
                              <dt>Sibling's SC</dt>
                              <dd>
                                {row.sibling_sc_first_name || row.sibling_sc_last_name
                                  ? [row.sibling_sc_first_name, row.sibling_sc_last_name].filter(Boolean).join(' ')
                                  : '—'}
                              </dd>
                            </>
                          )}
                          <dt>DCFS (foster)</dt><dd>{row.is_dcfs ? 'Yes' : 'No'}</dd>
                          {row.re_referral === true || row.re_referral === 'true' ? (
                            <>
                              <dt>Original SC</dt>
                              <dd>
                                {row.original_sc_first_name || row.original_sc_last_name
                                  ? [row.original_sc_first_name, row.original_sc_last_name].filter(Boolean).join(' ')
                                  : '—'}
                              </dd>
                            </>
                          ) : null}
                          <dt>Referral SC comment</dt><dd>{row.referral_sc_comment || '—'}</dd>
                          <dt>Race</dt><dd>{row.race_description || '—'}</dd>
                        </dl>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
              })
            )}
          </tbody>
        </table>
      </div>

      {pendingAssign && (() => {
        const newStaff = staff.find((s) => String(s.id) === String(pendingAssign.value));
        const clientName = [pendingAssign.row.first_name, pendingAssign.row.last_name].filter(Boolean).join(' ') || 'this client';
        const scName = newStaff ? [newStaff.first_name, newStaff.last_name].filter(Boolean).join(' ') : 'selected SC';
        return (
          <div className="sc-workload-overlay" onClick={() => setPendingAssign(null)}>
            <div className="sc-assign-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sc-workload-header">
                <h2>Confirm SC Assignment</h2>
                <button type="button" className="sc-workload-close" onClick={() => setPendingAssign(null)} aria-label="Cancel">×</button>
              </div>
              <div className="sc-assign-confirm-body">
                <p>Assign <strong>{scName}</strong> to <strong>{clientName}</strong>?</p>
                <label className="sc-assign-confirm-check">
                  <input
                    type="checkbox"
                    checked={pendingGenerateDocs}
                    onChange={(e) => setPendingGenerateDocs(e.target.checked)}
                  />
                  Generate assignment documents
                </label>
              </div>
              <div className="sc-assign-confirm-actions">
                <button type="button" className="sc-assign-btn-cancel" onClick={() => setPendingAssign(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="sc-assign-btn-confirm"
                  onClick={() => {
                    handleAssignStaff(pendingAssign.row, pendingAssign.value, pendingGenerateDocs);
                    setPendingAssign(null);
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {workloadModalOpen && (
        <div className="sc-workload-overlay" onClick={() => setWorkloadModalOpen(false)}>
          <div className="sc-workload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sc-workload-header">
              <h2>SC Workload</h2>
              <button type="button" className="sc-workload-close" onClick={() => setWorkloadModalOpen(false)} aria-label="Close">
                ×
              </button>
            </div>
            <p className="sc-workload-help">
              <strong>Total</strong> = assigned clients whose current referral is not closed (active). <strong>Intake</strong> = of those active clients, count where the current referral does not yet have the intake date set (INTAKE tracking item not completed).
            </p>
            {workloadLoading ? (
              <p className="sc-workload-loading">Loading…</p>
            ) : (
              <table className="sc-workload-table">
                <thead>
                  <tr>
                    <th>Service coordinator</th>
                    <th>Intake</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {workloadRows.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="no-data">No staff data</td>
                    </tr>
                  ) : (
                    workloadRows.map((row) => (
                      <tr key={row.id}>
                        <td>{[row.last_name, row.first_name].filter(Boolean).join(', ') || row.id}</td>
                        <td>{row.intake_count ?? 0}</td>
                        <td>{row.total_count ?? 0}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ScAssignmentList;
